<script setup lang="ts">
import { computed, nextTick, watch } from 'vue';
import { useFieldValue, useForm } from 'vee-validate';
import { omit } from 'lodash-es';
import {
  commercialIncomingPropertyTypeOptions,
  commercialOutgoingPropertyTypeOptions,
  quoteCommercialTypeOptions,
  LoanType,
  propertyDetailOptions,
  PropertyDetailOption,
  PropertyPurpose,
  propertyUsedOptions,
  PropertyType,
  residenceTypeOptions,
  residentialCommercialTypes,
  quoteResidentialCommercialTypes,
  incomingPropertyTypeOptions,
  supportingPropertyTooltip,
} from '@/helpers/const';
import { useFeatureFlags } from '@/helpers/useFeatureFlags';
import { useUserQuoteProcess } from '@/helpers/useUserQuoteProcess';
import { brokerPropertyDetailsSchema, brokerPropertyDetailsSimpleSchema } from '@/validation';

import PropertyToPurchaseFields from '@/views/broker-application/modules/PropertyDetails/PropertyToPurchaseFields.vue';
import PropertyToSellFields from '@/views/broker-application/modules/PropertyDetails/PropertyToSellFields.vue';
import SupportingPropertyFields from '@/views/broker-application/modules/PropertyDetails/SupportingPropertyFields.vue';

import type { PropertyDetailOptionType } from '@/helpers/const';
import type { PropertyDetailEntry } from '@/types';

const props = defineProps<{
  modelValue: PropertyDetailEntry;
  hasIncomingProperty?: boolean;
  defaultPropertyOptions?: PropertyDetailOptionType;
  isPropertyOptionsHidden?: boolean;
}>();

const { loanApp, sectionStates } = useUserQuoteProcess();
const { isCommercialQuoteFlowEnabled } = useFeatureFlags();
const loanType = computed(() => loanApp.value.loanType);

const emit = defineEmits<{
  (e: 'update:modelValue', value: PropertyDetailEntry): void;
}>();

const isSingleSecurity = computed(() => loanType.value === LoanType.SINGLE_SECURITY);
const isPurchaseOnly = computed(() => loanType.value === LoanType.PURCHASE_ONLY);

const isBridging = computed(() => loanType.value === LoanType.BRIDGING);

const propertyDetailsType = computed(() => {
  if (isBridging.value) return sectionStates.value.incomingProperties;

  if (isSingleSecurity.value) return sectionStates.value.outgoingProperties;

  return sectionStates.value.incomingProperties;
});

const validationSchema = computed(() =>
  propertyDetailsType.value === 'simple' ? brokerPropertyDetailsSimpleSchema : brokerPropertyDetailsSchema,
);

const { meta, submitCount, values, handleSubmit, resetForm, setFieldValue } = useForm<PropertyDetailEntry>({
  validateOnMount: false,
  validationSchema,
});

const propertyOptionsField = useFieldValue<string>('propertyOptions');
const idField = useFieldValue<string>('id');

const prefixMap: Record<string, string> = {
  [PropertyDetailOption.PROPERTY_TO_PURCHASE]: 'incoming-property',
  [PropertyDetailOption.PROPERTY_TO_SELL]: 'outgoing-property',
  [PropertyDetailOption.SUPPORTING_PROPERTY]: 'supporting-property',
};

const baseId = computed(() => {
  const index = idField.value?.split('-').pop() ?? '0';
  const prefix = prefixMap[propertyOptionsField.value ?? ''] ?? 'property';
  return `${prefix}-${index}`;
});

const isPropertyPurchaseRestricted = computed(() => {
  return (
    values.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE &&
    (values.propertyType === PropertyType.VACANT_LAND ||
      values.propertyType === PropertyType.RETIREMENT_LAND_OR_COMMUNITY_HOME)
  );
});

const propertyOptions = computed(() => values.propertyOptions);

const propertyOptionTooltips = computed<Record<string, string>>(() => {
  const tooltip = isCommercialQuoteFlowEnabled()
    ? supportingPropertyTooltip
    : "A 'Supporting Property' is an existing property that isn't being bought or sold, but is used as additional security for Loan To Value Ratio purposes.";
  return { [PropertyDetailOption.SUPPORTING_PROPERTY]: tooltip };
});

const commercialTypeTooltips: Record<string, string> = {
  SPECIALISED: 'Specialised Use includes childcare, gaming zone, medical clinic, and service stations.',
};

const availablePropertyOptions = computed(() => {
  const isCurrentlyPurchase = values.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE;
  const shouldHidePurchase = isSingleSecurity.value || (props.hasIncomingProperty && !isCurrentlyPurchase);

  const options = isPurchaseOnly.value
    ? omit(propertyDetailOptions, PropertyDetailOption.PROPERTY_TO_SELL)
    : propertyDetailOptions;

  return shouldHidePurchase ? omit(options, PropertyDetailOption.PROPERTY_TO_PURCHASE) : options;
});

const propertyTypeOptions = computed(() => {
  const isPurchase = values.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE;
  return isPurchase ? commercialIncomingPropertyTypeOptions : commercialOutgoingPropertyTypeOptions;
});

const commercialTypeOptions = computed(() => {
  if (values.propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY) {
    return isCommercialQuoteFlowEnabled() ? quoteCommercialTypeOptions : incomingPropertyTypeOptions;
  }
  return incomingPropertyTypeOptions;
});

const isResidentialCommercialType = computed(() => {
  if (values.propertyType !== PropertyType.COMMERCIAL) return false;
  if (isCommercialQuoteFlowEnabled()) {
    return (
      residentialCommercialTypes.includes(values.commercialType as (typeof residentialCommercialTypes)[number]) ||
      quoteResidentialCommercialTypes.has(values.commercialType ?? '')
    );
  }
  return residentialCommercialTypes.includes(values.commercialType as (typeof residentialCommercialTypes)[number]);
});

/** Show residence type only for non-granular residential commercial (supporting properties) */
const showResidenceTypeField = computed(
  () =>
    isResidentialCommercialType.value &&
    residentialCommercialTypes.includes(values.commercialType as (typeof residentialCommercialTypes)[number]),
);

const showPropertyLeasedQuestion = computed(
  () => !isPropertyPurchaseRestricted.value && values.propertyPurpose === PropertyPurpose.INVESTMENT_PROPERTY,
);

const toPostcodeAddress = (addr: PropertyDetailEntry['address']) => ({
  addressLine: '',
  addressLine2: '',
  locality: '',
  state: '',
  postcode: addr?.postcode ?? '',
  countryCode: 'AU',
});

const update = handleSubmit(
  () => {
    if (isPropertyPurchaseRestricted.value) {
      return false;
    }
    const payload = { ...values };
    if (propertyDetailsType.value === 'simple' && !isPropertyPurchaseRestricted.value && payload) {
      payload.address = toPostcodeAddress(payload.address);
    }
    emit('update:modelValue', payload);
    return true;
  },
  () => false,
);

const cancel = () => {
  resetForm();
};

watch(
  () => props.modelValue,
  (val) => {
    const initialValues = { ...val };
    if (!initialValues.propertyOptions && props.defaultPropertyOptions) {
      initialValues.propertyOptions = props.defaultPropertyOptions;
    }
    if (propertyDetailsType.value === 'simple' && initialValues) {
      initialValues.address = toPostcodeAddress(initialValues.address);
    }
    resetForm({ values: initialValues });
  },
  { immediate: true },
);

watch(
  () => [values.propertyType, values.commercialType] as const,
  async ([propertyType, commercialType]) => {
    const isResidential =
      propertyType === PropertyType.COMMERCIAL &&
      (residentialCommercialTypes.includes(commercialType as (typeof residentialCommercialTypes)[number]) ||
        (isCommercialQuoteFlowEnabled() && quoteResidentialCommercialTypes.has(commercialType ?? '')));
    if (isResidential) {
      await nextTick();
      setFieldValue('propertyPurpose', PropertyPurpose.INVESTMENT_PROPERTY);
    }
  },
  { flush: 'post' },
);

watch(showResidenceTypeField, (show) => {
  if (!show) {
    setFieldValue('idsType', undefined);
  }
});

defineExpose({ update, cancel, isPropertyPurchaseRestricted, propertyOptions });
</script>

<template>
  <form>
    <div class="property-details-form">
      <div v-if="!props.isPropertyOptionsHidden" class="form-control">
        <BxField v-slot="{ field }" name="propertyOptions">
          <BxSelect
            :id="`${baseId}-property-options`"
            :list-items="availablePropertyOptions"
            :option-tooltips="propertyOptionTooltips"
            :data-test-id="`${baseId}-property-options`"
            v-bind="field"
            label="Property Purpose"
            label-type="floating"
            placeholder="Select property purpose..."
          />
        </BxField>
      </div>
      <BxField v-slot="{ field }" name="propertyType">
        <BxSelect
          :id="`${baseId}-property-type`"
          :list-items="propertyTypeOptions"
          :data-test-id="`${baseId}-property-type`"
          v-bind="field"
          class="form-control"
          label="Property Type"
          label-type="floating"
          placeholder="Select property type..."
        />
      </BxField>
      <div v-if="isPropertyPurchaseRestricted" class="form-control mt-6">
        <p>
          This property type requires a single security bridging loan, please select the button below to complete the
          single security application.
        </p>
      </div>
      <div v-if="values.propertyType === PropertyType.COMMERCIAL" class="form-control">
        <BxField v-slot="{ field }" name="commercialType">
          <BxSelect
            :id="`${baseId}-commercial-type`"
            :list-items="commercialTypeOptions"
            :option-tooltips="commercialTypeTooltips"
            :data-test-id="`${baseId}-commercial-type`"
            v-bind="field"
            label="Commercial Type"
            label-type="floating"
            placeholder="Select commercial type..."
          />
        </BxField>
      </div>
      <BxField
        v-if="!isPropertyPurchaseRestricted && propertyDetailsType === 'comprehensive'"
        v-slot="{ field }"
        name="address"
      >
        <BxAddressInput
          :id="`${baseId}-property-address`"
          :data-test-id="`${baseId}-property-address`"
          v-bind="field"
          class="form-control"
          label="Property Address"
          label-type="floating"
        />
      </BxField>
      <BxField
        v-if="!isPropertyPurchaseRestricted && propertyDetailsType === 'simple'"
        v-slot="{ field }"
        name="address.postcode"
      >
        <BxInput
          :id="`${baseId}-postcode`"
          :data-test-id="`${baseId}-postcode`"
          v-bind="field"
          class="form-control"
          label="Postcode"
          label-type="floating"
          minlength="4"
          maxlength="4"
        />
      </BxField>
      <BxField
        v-if="!isPropertyPurchaseRestricted && !isResidentialCommercialType"
        v-slot="{ field }"
        name="propertyPurpose"
      >
        <BxRadio
          :id="`${baseId}-property-used`"
          :data-test-id="`${baseId}-property-used`"
          :options="propertyUsedOptions"
          v-bind="field"
          class="form-control"
          label="How is this property used?"
          label-type="default"
          block
        />
      </BxField>
      <BxField v-if="showPropertyLeasedQuestion" v-slot="{ field }" name="propertyLeased">
        <BxRadio
          :id="`${baseId}-property-leased`"
          :data-test-id="`${baseId}-property-leased`"
          v-bind="field"
          class="form-control"
          label="Is the property currently leased?"
          label-type="default"
          boolean
        />
      </BxField>
      <BxField v-if="showResidenceTypeField" v-slot="{ field }" name="idsType">
        <BxRadio
          :id="`${baseId}-residence-type`"
          :data-test-id="`${baseId}-residence-type`"
          :options="residenceTypeOptions"
          v-bind="field"
          class="form-control"
          label="What is the residence type?"
          label-type="default"
        />
      </BxField>
      <PropertyToPurchaseFields
        v-if="values.propertyOptions === PropertyDetailOption.PROPERTY_TO_PURCHASE && !isPropertyPurchaseRestricted"
        :id="baseId"
        :contract-for-sale="values.contractForSale"
        :receive-rental-income="values.receiveRentalIncome"
        :property-purpose="values.propertyPurpose"
        :is-new-build="values.isNewBuild"
      />
      <PropertyToSellFields
        v-if="values.propertyOptions === PropertyDetailOption.PROPERTY_TO_SELL"
        :id="baseId"
        :sale-status="values.saleStatus"
      />
      <SupportingPropertyFields
        v-if="values.propertyOptions === PropertyDetailOption.SUPPORTING_PROPERTY"
        :id="baseId"
        :property-purpose="values.propertyPurpose"
        :receive-rental-income="values.receiveRentalIncome"
        :is-new-build="values.isNewBuild"
      />
    </div>
    <BxErrorPanel v-show="!meta.valid && submitCount > 0" class="mt-6">
      Please review the error messages above.
    </BxErrorPanel>
  </form>
</template>

<style scoped>
.property-details-form :deep(.form-control) {
  padding-right: 2.5rem;
}
</style>
